import React, { cloneElement, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import once from 'lodash/once';
import { envType } from '@atlassiansox/analytics-web-client';
import {
	Coordination,
	CoordinationClient as EngagekitCoordinationClient,
} from '@atlassiansox/engagekit-ts';
import { usePostOfficeExernalMessageProxy } from '@atlassian/post-office-context';
import { MessageDeliveryStatus, ProductIds } from '@atlassian/ipm-choreographer';
import {
	ChoreographerContextProvider,
	useChoreographedStatusContext,
} from '@atlassian/jira-choreographer-services/src/ChoreographerContextProvider.tsx';
import type { MaybeChoreographedComponentProps } from '@atlassian/jira-choreographer-services/src/types.tsx';
import { shouldBeChoreographed } from '@atlassian/jira-choreographer-services/src/utils.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@atlassian/jira-get-meta';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { toEnvironment } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

export type Props = {
	shouldAllowOnSynthetic?: boolean;
	messageId: string;
	children?: ReactNode;
	fallback?: ReactNode;
};

const isSyntheticTenant = () => getMeta('ajs-is-synthetic') === 'true';
const getEnvironmentType = once(() => {
	const env = toEnvironment(getMeta('ajs-environment'));
	if (env === 'prod') {
		return envType.PROD;
	}
	if (env === 'staging') {
		return envType.STAGING;
	}
	return envType.DEV;
});

// This is a modified CoordinationClient that does not allow external messages to show on
// synthetic tenants, due to the unexpected interruptions they can cause on tests (by overlapping page elements ).
// If required, you can opt in to show your external message on synthetic tenants using the `shouldAllowOnSynthetic` argument.
export class CoordinationClient extends EngagekitCoordinationClient {
	shouldUseMockClient: boolean;

	isDebugModeEnabled: boolean;

	enableChoreographer: boolean;

	onMessageDisposition: MaybeChoreographedComponentProps['onMessageDisposition'];

	constructor(
		cloudId: string,
		stargateUrl: string,
		shouldAllowOnSynthetic = false,
		orgId = '',
		messageType: MaybeChoreographedComponentProps['messageType'] = 'transactional',
		onMessageDisposition: MaybeChoreographedComponentProps['onMessageDisposition'] = undefined,
		isAlreadyChoreographedAtAncestor = false,
	) {
		// If a ancestor (e.g. CoordinationClient wraps CoordinationClient) is already choreographed,
		// we should not re-choreograph this CoordinationClient
		const enableChoreographer =
			shouldBeChoreographed({ messageType }) && !isAlreadyChoreographedAtAncestor;

		const coordinationOptions = {
			enableChoreographer,
			envType: getEnvironmentType(),
			additionalData: { sourceProductId: ProductIds.JIRA },
		};

		super({ cloudId, stargateUrl, orgId, isFedRAMPEnvironment: false, coordinationOptions });
		this.shouldUseMockClient = isSyntheticTenant() && !shouldAllowOnSynthetic;
		this.enableChoreographer = enableChoreographer;
		this.onMessageDisposition = onMessageDisposition;
		// // This is a permanant flag: go/jira-ep-debugging
		this.isDebugModeEnabled = fg('ep-external-message-debug-mode');
	}

	async start(messageId: string): Promise<boolean> {
		if (__SERVER__) {
			// coordination clients are inactive on the server
			return false;
		}

		if (this.isDebugModeEnabled) {
			const serverResponse = await super.start(messageId);
			if (this.enableChoreographer) {
				this.onMessageDisposition?.(
					serverResponse ? MessageDeliveryStatus.STARTED : MessageDeliveryStatus.BLOCKED,
				);
			}
			// eslint-disable-next-line no-alert, no-restricted-globals
			const shouldShowMessage = confirm(
				`🐞 𝗘𝗣 𝗘𝘅𝘁𝗲𝗿𝗻𝗮𝗹 𝗠𝗲𝘀𝘀𝗮𝗴𝗲 𝗗𝗲𝗯𝘂𝗴𝗴𝗲𝗿

Message ID: ${messageId}
Start call server response: ${serverResponse}

Do you want to display the message?`,
			);
			if (!shouldShowMessage) {
				const didStop = await super.stop(messageId);
				if (this.enableChoreographer) {
					this.onMessageDisposition?.(
						didStop ? MessageDeliveryStatus.STOPPED : MessageDeliveryStatus.BLOCKED,
					);
				}
			}
			return shouldShowMessage;
		}
		return this.shouldUseMockClient
			? false // never allow messages to start
			: super.start(messageId).then((didStart) => {
					if (this.enableChoreographer) {
						this.onMessageDisposition?.(
							didStart ? MessageDeliveryStatus.STARTED : MessageDeliveryStatus.BLOCKED,
						);
					}
					return didStart;
				});
	}

	async stop(messageId: string): Promise<boolean> {
		if (__SERVER__) {
			// coordination clients are inactive on the server
			return false;
		}

		return this.shouldUseMockClient
			? true // always allow messages to stop
			: super.stop(messageId).then((didStop) => {
					if (this.enableChoreographer) {
						this.onMessageDisposition?.(
							didStop ? MessageDeliveryStatus.STOPPED : MessageDeliveryStatus.BLOCKED,
						);
					}
					return didStop;
				});
	}
}
/**
 * This Component passes a "stop" prop to it's children which you can call as a function in order to
 * declare that you are done with showing your message.
 * Please refer to playbook/coordination-service.md for more details.
 */
function ClientOnlyCoordinationClientComponent(
	props: Props & Partial<MaybeChoreographedComponentProps>,
) {
	const cloudId = useCloudId();
	const stargateUrl = '/gateway/api';
	const { data: orgId } = useOrgId();

	const { isAlreadyChoreographedAtAncestor } = useChoreographedStatusContext();

	const client = useMemo(
		() =>
			new CoordinationClient(
				cloudId,
				stargateUrl,
				props.shouldAllowOnSynthetic,
				orgId,
				props.messageType,
				props.onMessageDisposition,
				isAlreadyChoreographedAtAncestor,
			),
		[
			cloudId,
			isAlreadyChoreographedAtAncestor,
			orgId,
			props.messageType,
			props.onMessageDisposition,
			props.shouldAllowOnSynthetic,
		],
	);

	const { fallback, messageId } = props;

	return (
		<ChoreographerContextProvider isChoreographed={client.enableChoreographer}>
			<Coordination
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				client={client as CoordinationClient}
				fallback={fallback}
				messageId={messageId}
			>
				{/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions */}
				{props.children as JSX.Element}
			</Coordination>
		</ChoreographerContextProvider>
	);
}

export default function CoordinationClientComponent(
	props: Props & Partial<MaybeChoreographedComponentProps>,
): React.JSX.Element {
	if (__SERVER__) {
		return <>{props.fallback}</>;
	}
	return <ClientOnlyCoordinationClientComponent {...props} />;
}

export function PostOfficeCoordinationComponent(
	props: PostOfficeCoordinationProps,
): React.JSX.Element {
	if (__SERVER__) {
		return <>{props.fallback}</>;
	}
	return <PostOfficeCoordination {...props} />;
}

/**
 * This component hooks into the Post Office ep-migration-external-messages message template.
 * Your message info must be added to message list here:
 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/post-office/integrated-teams/post-office-team/message-templates/ep-migration-external-messages/src/implicit-message/messages.ts
 */
export function PostOfficeCoordination({
	fallback = null,
	children,
	messageId,
}: PostOfficeCoordinationProps) {
	const { hasMessageStarted, requestMessageStart, dismissMessage } =
		usePostOfficeExernalMessageProxy(messageId);

	useEffect(() => {
		requestMessageStart();
	}, [requestMessageStart, messageId]);

	const stop = useCallback(() => {
		dismissMessage();
	}, [dismissMessage]);

	return hasMessageStarted ? cloneElement(children, { stop }) : fallback;
}

type PostOfficeCoordinationProps = {
	fallback: ReactNode | null;
	children: JSX.Element;
	messageId: string;
};
